import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  BookUser,
  Building2,
  Loader2,
  Mail,
  Phone,
  Search,
  ShieldAlert,
  Truck,
  Users,
  UsersRound,
} from 'lucide-react';
import { fetchRepertoire, type RepertoireLigne, type RepertoireVolet } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { usePersonnalisation } from '../hooks/usePersonnalisation';
import { euros } from '../utils/format';

const jourCourt = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/** Ce qu'un volet raconte de lui-même, avant même d'être chargé. */
const VOLETS: Record<
  RepertoireVolet,
  { titre: string; icone: typeof Users; sousTitre: string; vide: string }
> = {
  association: {
    titre: 'Association',
    icone: Users,
    sousTitre: 'Le bureau et les bénévoles, avec leurs compétences déclarées.',
    vide: "Personne d'autre que vous pour l'instant.",
  },
  adherents: {
    titre: 'Adhérents',
    icone: UsersRound,
    sousTitre: "Le registre des adhérents actifs, et le numéro à appeler en cas d'urgence.",
    vide: 'Aucun adhérent actif au registre.',
  },
  fournisseurs: {
    titre: 'Fournisseurs',
    icone: Truck,
    sousTitre: 'Chez qui l\'association achète, ce qu\'elle y a dépensé et depuis quand.',
    vide: "Aucun fournisseur : les achats saisis n'en nomment aucun.",
  },
  sponsors: {
    titre: 'Sponsors',
    icone: Building2,
    sousTitre: 'Les coordonnées de nos partenaires, sans passer par la facturation.',
    vide: 'Aucun sponsor enregistré.',
  },
};

/**
 * Le répertoire : quatre populations, une seule question.
 *
 * « Quel est son numéro ? » se posait de la même façon pour un bénévole, un
 * adhérent, un fournisseur et un sponsor — et se cherchait dans quatre écrans
 * bâtis pour autre chose. On y trouvait le numéro, après l'avoir cherché
 * parmi des colonnes qui ne servent pas à appeler.
 *
 * Cette page ne remplace aucun de ces écrans : elle en extrait ce qu'on vient
 * y prendre en urgence, et le rend de la même manière pour les quatre. Le
 * téléphone et le courriel sont des liens : sur un mobile, au bord du terrain
 * ou derrière le bar, on appelle en un geste.
 */
export default function RepertoirePage({ volet }: { volet: RepertoireVolet }) {
  const { user, isLoading: authLoading, aLeDroit } = useAuth();
  const [recherche, setRecherche] = useState('');

  const estBureau =
    user?.roles?.includes('ROLE_BUREAU') === true || user?.roles?.includes('ROLE_ADMIN') === true;
  const estBenevole = user?.roles?.includes('ROLE_VOLUNTEER') === true || estBureau;

  // Une option non souscrite n'a pas à laisser d'onglet derrière elle : sans
  // gestion des adhérents, le volet n'aurait personne à montrer, et un écran
  // qui explique pourquoi il est vide vaut moins qu'un écran absent.
  const perso = usePersonnalisation();
  const optionOuverte = (nom: string) => (perso?.options as any)?.[nom] !== false;

  // Chaque volet garde le droit de l'écran dont il tire ses données : un
  // répertoire raccourcit un chemin, il n'en ouvre pas de nouveau.
  const droits: Record<RepertoireVolet, boolean> = {
    association: estBenevole,
    adherents: aLeDroit('voir_adherents') && optionOuverte('licences'),
    fournisseurs: aLeDroit('suivre_achats'),
    sponsors: aLeDroit('voir_sponsors'),
  };
  const aAcces = droits[volet];

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['repertoire', volet],
    queryFn: () => fetchRepertoire(volet),
    enabled: aAcces,
    retry: false,
  });

  const lignes = useMemo(() => {
    const toutes = data?.lignes ?? [];
    const terme = recherche.trim().toLowerCase();
    if (terme === '') return toutes;
    // On cherche par tout ce qui est affiché — un nom à moitié retenu, une
    // ville, une compétence, un bout de numéro.
    return toutes.filter((l) =>
      [l.nom, l.fonction, l.ville, l.email, l.telephone, ...(l.competences ?? [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(terme)
    );
  }, [data, recherche]);

  const meta = VOLETS[volet];
  const Icone = meta.icone;

  if (authLoading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <BookUser className="w-7 h-7 text-primary-700 shrink-0" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Répertoire — {meta.titre}
          </h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">{meta.sousTitre}</p>
        </div>
      </div>

      <Onglets courant={volet} droits={droits} />

      {!aAcces ? (
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              Ce volet du répertoire dépend de votre fonction au bureau : un administrateur peut
              l'ouvrir dans la grille de droits.
            </p>
          </div>
        </div>
      ) : isError ? (
        <div className="card bg-amber-50 border-amber-200 text-amber-900 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="text-sm">{(error as Error).message}</p>
        </div>
      ) : isPending ? (
        <div className="p-8 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[14rem]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Un nom, une ville, une compétence…"
                className="input pl-9"
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
              {lignes.length} sur {data.lignes.length}
            </p>
          </div>

          {/* Le volet fournisseurs se sait provisoire, et le dit plutôt que
              de laisser croire à un annuaire tenu à la main. */}
          {data.derive && (
            <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2">
              Cette liste est reconstituée à partir des noms saisis sur les achats — il n'y a pas
              encore de fiche fournisseur avec ses coordonnées. Deux graphies d'un même nom
              comptent donc pour deux.
            </p>
          )}

          {lignes.length === 0 ? (
            <div className="card text-center py-10 space-y-2">
              <Icone className="w-8 h-8 mx-auto text-gray-300" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {recherche.trim() === '' ? meta.vide : 'Aucun résultat pour cette recherche.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {lignes.map((ligne) => (
                <Carte key={`${volet}-${ligne.id ?? ligne.nom}`} ligne={ligne} volet={volet} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Les quatre volets. Ceux qu'on n'a pas le droit d'ouvrir ne paraissent pas :
 * un onglet grisé n'apprend rien qu'une absence n'apprenne mieux.
 */
function Onglets({
  courant,
  droits,
}: {
  courant: RepertoireVolet;
  droits: Record<RepertoireVolet, boolean>;
}) {
  const ordre: RepertoireVolet[] = ['association', 'adherents', 'fournisseurs', 'sponsors'];
  // Seuls les volets ouverts paraissent. On n'ajoute plus celui qu'on regarde
  // s'il est fermé : arriver par l'adresse sur un volet qu'on n'a pas ne doit
  // pas le faire apparaître dans la barre, ni pour soi ni pour les suivants.
  const visibles = ordre.filter((v) => droits[v]);

  return (
    <div className="flex flex-wrap gap-2">
      {visibles.map((v) => {
        const Icone = VOLETS[v].icone;
        const actif = v === courant;
        return (
          <Link
            key={v}
            to={`/repertoire/${v}`}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              actif
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary-300'
            }`}
          >
            <Icone className="w-4 h-4" />
            {VOLETS[v].titre}
          </Link>
        );
      })}
    </div>
  );
}

/**
 * Une fiche du répertoire.
 *
 * Même carte pour les quatre populations : le nom, ce qui situe la personne,
 * puis les deux liens qui servent. Ce qui diffère d'un volet à l'autre passe
 * en bas, sans changer la forme — on apprend à lire la carte une fois.
 */
function Carte({ ligne, volet }: { ligne: RepertoireLigne; volet: RepertoireVolet }) {
  const initiales = ligne.nom
    .split(/\s+/)
    .slice(0, 2)
    .map((mot) => mot.charAt(0).toUpperCase())
    .join('');

  return (
    <div className="card space-y-2.5">
      <div className="flex items-start gap-3">
        {ligne.photoUrl ? (
          <img
            src={ligne.photoUrl}
            alt=""
            className="w-10 h-10 rounded-full object-cover shrink-0"
          />
        ) : (
          <span className="w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 grid place-items-center text-sm font-semibold shrink-0">
            {initiales || '—'}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">{ligne.nom}</p>
          {ligne.fonction && (
            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{ligne.fonction}</p>
          )}
          {ligne.ville && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{ligne.ville}</p>
          )}
        </div>
        {ligne.estBureau && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 shrink-0">
            Bureau
          </span>
        )}
      </div>

      {/* Des liens, pas du texte : sur un mobile on appelle en un geste. */}
      <div className="flex flex-wrap gap-2">
        {ligne.telephone ? (
          <a
            href={`tel:${ligne.telephone.replace(/\s/g, '')}`}
            className="inline-flex items-center gap-1.5 text-sm text-primary-700 dark:text-primary-300 hover:underline"
          >
            <Phone className="w-3.5 h-3.5" />
            {ligne.telephone}
          </a>
        ) : (
          <span className="text-sm text-gray-400">Pas de téléphone</span>
        )}
        {ligne.email && (
          <a
            href={`mailto:${ligne.email}`}
            className="inline-flex items-center gap-1.5 text-sm text-primary-700 dark:text-primary-300 hover:underline min-w-0"
          >
            <Mail className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{ligne.email}</span>
          </a>
        )}
      </div>

      {(ligne.competences?.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-1">
          {ligne.competences!.map((c) => (
            <span
              key={c}
              className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
            >
              {c}
            </span>
          ))}
        </div>
      )}

      {/* Pour un mineur, le numéro utile n'est pas le sien. Il est donc à
          côté, et non derrière une fiche à ouvrir. */}
      {(ligne.urgences?.length ?? 0) > 0 && (
        <div className="pt-2 border-t border-gray-100 dark:border-gray-800 space-y-1">
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
            <ShieldAlert className="w-3.5 h-3.5" /> En cas d'urgence
          </p>
          {ligne.urgences!.map((u, i) => (
            <a
              key={i}
              href={`tel:${u.telephone.replace(/\s/g, '')}`}
              className="block text-sm text-gray-700 dark:text-gray-300 hover:underline"
            >
              {u.nom}
              {u.lien ? ` (${u.lien})` : ''} — {u.telephone}
            </a>
          ))}
        </div>
      )}

      {volet === 'fournisseurs' && (
        <div className="pt-2 border-t border-gray-100 dark:border-gray-800 text-sm space-y-0.5">
          <p className="flex items-baseline justify-between gap-2">
            <span className="text-gray-500 dark:text-gray-400">
              {ligne.achats} achat{(ligne.achats ?? 0) > 1 ? 's' : ''}
            </span>
            <span className="font-semibold tabular-nums">{euros.format(ligne.total ?? 0)}</span>
          </p>
          {(ligne.aPayer ?? 0) > 0 && (
            <p className="flex items-baseline justify-between gap-2 text-red-600">
              <span>reste à payer</span>
              <span className="font-semibold tabular-nums">{euros.format(ligne.aPayer!)}</span>
            </p>
          )}
          {ligne.dernierAchat && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              Dernier le {jourCourt.format(new Date(ligne.dernierAchat))}
              {ligne.dernierLibelle ? ` — ${ligne.dernierLibelle}` : ''}
            </p>
          )}
        </div>
      )}

      {volet === 'sponsors' && (ligne.souscriptions ?? 0) > 0 && (
        <div className="pt-2 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400">
          {ligne.souscriptions} souscription{(ligne.souscriptions ?? 0) > 1 ? 's' : ''}
          {ligne.derniereSouscription
            ? `, la dernière le ${jourCourt.format(new Date(ligne.derniereSouscription))}`
            : ''}
          {ligne.siteWeb && (
            <>
              {' · '}
              <a
                href={ligne.siteWeb.startsWith('http') ? ligne.siteWeb : `https://${ligne.siteWeb}`}
                target="_blank"
                rel="noopener"
                className="text-primary-700 dark:text-primary-300 hover:underline"
              >
                son site
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
}
